/**
 * A durable identifier for THIS browser profile, used only to report the notification permission.
 *
 * A device that DENIES notification permission has no push subscription, so it cannot be
 * identified the way every other part of this feature identifies a device — by its endpoint. That
 * is the whole problem the permission record exists to solve: without an id of our own, a refusal is
 * indistinguishable from a browser that was never asked, and an admin sees nothing at all.
 *
 * It lives in `localStorage`, NOT `sessionStorage` (which `tab-id.ts` uses): a permission belongs to the
 * browser, not to a tab, and must survive both reloads and new tabs — otherwise every tab would
 * report itself as a separate device and one refusal would multiply into many.
 *
 * <p>WHAT IT IS NOT: it is not an identity, and it says nothing about a person. It is a random
 * UUID, scoped to one browser profile, sent only alongside a permission state. Clearing site data
 * yields a new id, which is the correct reading — as far as the permission goes that is a fresh browser,
 * and the browser will be asked again.
 */

import { readStorage, writeStorage } from 'src/app/shared/utils/safe-storage';

/** localStorage key. */
export const PUSH_DEVICE_ID_STORAGE_KEY = 'push.deviceId';

/**
 * localStorage key for the secret that PROVES this browser owns the row its device id names.
 *
 * The id above carries no secrecy requirement of any kind — it is echoed to the server on every
 * heartbeat — while registration deliberately reads a row owned by somebody else as a HANDOVER (a
 * shared terminal really does change hands). Those two together mean an id alone is enough to move
 * another user's row and silence their browser. So `POST /v2/push/devices` hands a row a secret when
 * it is created, returns it EXACTLY ONCE (only the SHA-256 is stored server-side) and expects it
 * back on every later registration.
 *
 * <p>It is a bearer credential for one push row and nothing else: it names no user, grants no API
 * access, and the worst a leak does is let its holder claim delivery for that one browser. It lives
 * beside the id for the same reason the id lives in `localStorage` — it belongs to the browser
 * profile, not to a tab or a session — and it is carried over the teardown wipe WITH the id
 * (`app.component.ts`), because an id that outlives its secret is an id this browser can no longer
 * prove it owns.
 */
export const PUSH_DEVICE_SECRET_STORAGE_KEY = 'push.deviceSecret';

/**
 * The id when localStorage will not hold it.
 *
 * Same reasoning as `tab-id.ts`: a refused write makes every read miss and mint a fresh id per
 * call, so one browser would report itself as an endless series of devices — each refusal
 * appearing as a new machine on the admin's screen. Losing it on reload is the accepted
 * degradation; multiplying rows is not.
 */
let inMemoryDeviceId: string | null = null;

/**
 * The secret when localStorage will not hold it — and the copy that WINS over the stored one.
 *
 * Same precedence rule `tab-id.ts` states for its own id, and it matters in the same place: while
 * storage is refusing writes, whatever is stored is the value the backend has already replaced, and
 * presenting a superseded secret proves nothing. Losing it on reload is the accepted degradation —
 * a client that cannot produce its secret is re-issued one for a row it already owns, which is
 * exactly the "lost localStorage" case the backend allows.
 */
let inMemoryDeviceSecret: string | null = null;

/**
 * Reset between specs — module state would otherwise leak the first id into every later spec.
 *
 * Clears the secret WITH the id, because they are one pair: a spec that reset the id and inherited
 * the previous spec's secret would present a fresh device id alongside a secret minted for another
 * row.
 */
export function resetPushDeviceMemory(): void {
  inMemoryDeviceId = null;
  inMemoryDeviceSecret = null;
}

/** The id this browser reports its permission under, minting one on first use. */
export function getPushDeviceId(): string {
  if (inMemoryDeviceId) {
    return inMemoryDeviceId;
  }
  const stored = readStorage('local', PUSH_DEVICE_ID_STORAGE_KEY);
  if (stored) {
    inMemoryDeviceId = stored;
    return stored;
  }
  const minted = mint();
  inMemoryDeviceId = minted;
  writeStorage('local', PUSH_DEVICE_ID_STORAGE_KEY, minted);
  return minted;
}

/** UUID where available; a timestamp+random id where it is not (older Safari, insecure origins). */
function mint(): string {
  const cryptoApi = (globalThis as unknown as Record<string, any>)['crypto'];
  if (cryptoApi && typeof cryptoApi['randomUUID'] === 'function') {
    return cryptoApi['randomUUID']();
  }
  return `d${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * The secret to present with this browser's device id, or `null` when it has not been given one.
 *
 * NEVER mints: unlike the id, the value can only come from a registration response. A locally
 * invented one would match no row's hash and would simply read as a failed proof.
 */
export function getPushDeviceSecret(): string | null {
  if (inMemoryDeviceSecret) {
    return inMemoryDeviceSecret;
  }
  const stored = readStorage('local', PUSH_DEVICE_SECRET_STORAGE_KEY);
  if (stored) {
    inMemoryDeviceSecret = stored;
    return stored;
  }
  return null;
}

/**
 * Store a secret a registration just minted. Memory first, then storage — the write is the half
 * that can be refused, and the value exists exactly once, in the response being handled.
 *
 * A blank value is ignored rather than stored: the backend OMITS the field whenever the caller
 * proved possession, which means "keep the one you have", so writing `''` over it would throw the
 * proof away on the very call that accepted it.
 */
export function setPushDeviceSecret(secret: string | null | undefined): void {
  if (!secret || !secret.trim()) {
    return;
  }
  inMemoryDeviceSecret = secret;
  writeStorage('local', PUSH_DEVICE_SECRET_STORAGE_KEY, secret);
}
